import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ArrowLeft, SpinnerGap, User, Briefcase, Phone, Envelope, MapPin, Buildings, TreeStructure, Target, TrendUp, CurrencyInr, Handshake, Warning, CalendarBlank, FunnelSimple, LinkedinLogo, Eye, ChartBar } from '@phosphor-icons/react';
import { fmtINR, fmtUSD } from '../utils/currency';
import { isApprovedStatus } from '../utils/placementStatus';
import VendorClaimsWidget from '../components/VendorClaimsWidget';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ROLE_LABELS = {
  superadmin: 'Super Admin', ceo: 'CEO', sales_manager: 'Sales Manager',
  recruiting_manager: 'Recruiting Manager', onsite_manager: 'Onsite Manager',
  sales_assistant_manager: 'Sales Asst. Manager', recruiting_assistant_manager: 'Recruiting Asst. Manager',
  sales_lead: 'Sales Lead', recruiting_lead: 'Recruiting Lead',
  sales_recruiter: 'Sales Recruiter', sales_technical_recruiter: 'Sales Recruiter & Technical Recruiter', technical_recruiter: 'Technical Recruiter',
};

const ROLE_COLORS = {
  superadmin: 'bg-slate-800 text-white', ceo: 'bg-blue-700 text-white',
  sales_manager: 'bg-indigo-100 text-indigo-700', recruiting_manager: 'bg-teal-100 text-teal-700',
  onsite_manager: 'bg-orange-100 text-orange-700',
  sales_lead: 'bg-blue-100 text-blue-700', recruiting_lead: 'bg-cyan-100 text-cyan-700',
  sales_recruiter: 'bg-amber-100 text-amber-700', sales_technical_recruiter: 'bg-amber-100 text-purple-700', technical_recruiter: 'bg-purple-100 text-purple-700',
};

const STATUS_COLORS = {
  approved: 'bg-emerald-100 text-emerald-700',
  sa_approved: 'bg-emerald-200 text-emerald-800',
  pending_lead: 'bg-amber-100 text-amber-700',
  pending_manager: 'bg-orange-100 text-orange-700',
  rejected: 'bg-red-100 text-red-700',
  backed_out: 'bg-red-50 text-red-600',
};

const fUSD = (n) => `$${(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmt = (n) => typeof n === 'number' ? n.toLocaleString('en-IN') : '0';

const COSTS = { ceipal_seat: 6, cb_view: 0.24, monster_view: 0.21, linkedin_slot: 80.10 };

const RateBadge = ({ rate }) => {
  if (!rate) return <span className="text-slate-300 text-xs">0%</span>;
  const c = rate >= 40 ? 'emerald' : rate >= 25 ? 'blue' : rate >= 15 ? 'amber' : 'red';
  return <span className={`px-2 py-0.5 rounded text-xs font-bold bg-${c}-50 text-${c}-700 border border-${c}-200`}>{rate}%</span>;
};

const PortalUtilizationSection = ({ utilization }) => {
  if (!utilization) return <Card className="border-slate-200"><CardContent className="py-10 text-center text-slate-400 text-sm">No portal utilization data available</CardContent></Card>;
  
  const li = utilization.linkedin || {};
  const cei = utilization.ceipal || {};
  const ind = utilization.indeed || {};
  const hasLi = li.total_profiles_viewed > 0 || li.inmails_sent > 0 || li.total_searches > 0;
  const hasCei = (cei.total_views || 0) > 0;
  const hasInd = (ind.jobs_posted || 0) > 0;
  const hasAny = hasLi || hasCei || hasInd;
  
  if (!hasAny) return <Card className="border-slate-200"><CardContent className="py-10 text-center text-slate-400 text-sm"><ChartBar size={28} className="mx-auto mb-2 text-slate-300" />No portal utilization data for this user</CardContent></Card>;

  // Cost calculation
  const liCost = (li.total_active_days || 0) > 0 ? COSTS.linkedin_slot : 0;
  const cbCost = (cei.career_builder || 0) * COSTS.cb_view;
  const mnCost = (cei.monster || 0) * COSTS.monster_view;
  const ceiCost = hasCei ? cbCost + mnCost + COSTS.ceipal_seat : 0;
  const totalCost = liCost + ceiCost;
  
  return (
    <div className="space-y-4" data-testid="portal-utilization-section">
      {/* Cost Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="border-slate-200 bg-slate-900"><CardContent className="p-3">
          <p className="text-[10px] uppercase text-slate-400 font-semibold">Monthly Cost</p>
          <p className="text-xl font-bold text-emerald-400">{fUSD(totalCost)}</p>
        </CardContent></Card>
        <Card className="border-slate-200"><CardContent className="p-3">
          <p className="text-[10px] uppercase text-slate-400 font-semibold">Quarterly Cost</p>
          <p className="text-xl font-bold text-sky-700">{fUSD(totalCost * 3)}</p>
        </CardContent></Card>
        {hasLi && <Card className="border-blue-200 bg-blue-50/30"><CardContent className="p-3">
          <p className="text-[10px] uppercase text-blue-500 font-semibold">LinkedIn RPS</p>
          <p className="text-xl font-bold text-blue-700">{fUSD(liCost)}</p>
          <p className="text-[10px] text-blue-400">/month</p>
        </CardContent></Card>}
        {hasCei && <Card className="border-purple-200 bg-purple-50/30"><CardContent className="p-3">
          <p className="text-[10px] uppercase text-purple-500 font-semibold">Ceipal</p>
          <p className="text-xl font-bold text-purple-700">{fUSD(ceiCost)}</p>
          <p className="text-[10px] text-purple-400">/month</p>
        </CardContent></Card>}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* LinkedIn Detail */}
        <Card className="border-blue-200">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-2"><LinkedinLogo size={16} weight="fill" className="text-blue-600" /><span className="text-xs font-bold uppercase text-blue-700">LinkedIn RPS</span></div>
            {hasLi ? (
              <div className="grid grid-cols-2 gap-2">
                <div className="p-2 bg-blue-50 rounded border border-blue-100 text-center">
                  <p className="text-[9px] text-blue-400 uppercase">Profiles Opened</p>
                  <p className="text-lg font-bold text-blue-800">{fmt(li.total_profiles_viewed || 0)}</p>
                </div>
                <div className="p-2 bg-slate-50 rounded border border-slate-100 text-center">
                  <p className="text-[9px] text-slate-400 uppercase">Searches Made</p>
                  <p className="text-lg font-bold text-slate-800">{fmt(li.total_searches || 0)}</p>
                </div>
                <div className="p-2 bg-emerald-50 rounded border border-emerald-100 text-center">
                  <p className="text-[9px] text-emerald-400 uppercase">InMails Sent</p>
                  <p className="text-lg font-bold text-emerald-800">{fmt(li.inmails_sent || 0)}</p>
                </div>
                <div className="p-2 bg-slate-50 rounded border border-slate-100 text-center">
                  <p className="text-[9px] text-slate-400 uppercase">InMails Accepted</p>
                  <p className="text-lg font-bold text-emerald-700">{fmt(li.inmails_accepted || 0)}</p>
                </div>
                <div className="p-2 bg-slate-50 rounded border border-slate-100 text-center">
                  <p className="text-[9px] text-slate-400 uppercase">Jobs Posted</p>
                  <p className="text-lg font-bold text-slate-800">{fmt(li.jobs_posted || 0)}</p>
                </div>
                <div className="p-2 bg-slate-50 rounded border border-slate-100 text-center">
                  <p className="text-[9px] text-slate-400 uppercase">Response Rate</p>
                  <RateBadge rate={li.inmail_response_rate} />
                </div>
                <div className="col-span-2 p-2 bg-blue-50 rounded border border-blue-200 text-center">
                  <p className="text-[9px] text-blue-400 uppercase">Active Days</p>
                  <p className="text-sm font-bold text-blue-800">{li.total_active_days || 0} days</p>
                </div>
              </div>
            ) : <p className="text-xs text-slate-400">No LinkedIn data</p>}
          </CardContent>
        </Card>

        {/* Ceipal Detail */}
        <Card className="border-purple-200">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-2"><Eye size={16} className="text-purple-600" /><span className="text-xs font-bold uppercase text-purple-700">Ceipal</span></div>
            {hasCei ? (
              <div className="grid grid-cols-2 gap-2">
                <div className="p-2 bg-purple-50 rounded border border-purple-100 text-center">
                  <p className="text-[9px] text-purple-400 uppercase">Career Builder Views</p>
                  <p className="text-lg font-bold text-purple-800">{fmt(cei.career_builder || 0)}</p>
                  <p className="text-[9px] text-slate-400">{fUSD((cei.career_builder || 0) * COSTS.cb_view)}</p>
                </div>
                <div className="p-2 bg-slate-50 rounded border border-slate-100 text-center">
                  <p className="text-[9px] text-slate-400 uppercase">Monster Views</p>
                  <p className="text-lg font-bold text-slate-800">{fmt(cei.monster || 0)}</p>
                  <p className="text-[9px] text-slate-400">{fUSD((cei.monster || 0) * COSTS.monster_view)}</p>
                </div>
                <div className="p-2 bg-slate-50 rounded border border-slate-100 text-center">
                  <p className="text-[9px] text-slate-400 uppercase">Internal DB Lookups</p>
                  <p className="text-lg font-bold text-slate-800">{fmt(cei.internal_db || 0)}</p>
                </div>
                <div className="p-2 bg-purple-50 rounded border border-purple-200 text-center">
                  <p className="text-[9px] text-purple-400 uppercase">Total Views</p>
                  <p className="text-lg font-bold text-purple-800">{fmt(cei.total_views || 0)}</p>
                </div>
              </div>
            ) : <p className="text-xs text-slate-400">No Ceipal data</p>}
          </CardContent>
        </Card>

        {/* Indeed Detail */}
        <Card className="border-amber-200">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-2"><Briefcase size={16} className="text-amber-600" /><span className="text-xs font-bold uppercase text-amber-700">Indeed</span></div>
            {hasInd ? (
              <div className="grid grid-cols-2 gap-2">
                <div className="p-2 bg-amber-50 rounded border border-amber-100 text-center">
                  <p className="text-[9px] text-amber-400 uppercase">Jobs Posted</p>
                  <p className="text-lg font-bold text-amber-800">{fmt(ind.jobs_posted || 0)}</p>
                </div>
                <div className="p-2 bg-slate-50 rounded border border-slate-100 text-center">
                  <p className="text-[9px] text-slate-400 uppercase">Low-end</p>
                  <p className="text-lg font-bold text-slate-800">{fmt(ind.jobs_low_end || 0)}</p>
                </div>
              </div>
            ) : <p className="text-xs text-slate-400">No Indeed data</p>}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const UserProfilePage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { isSuperadmin, isCeo } = useAuth();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [activeTab, setActiveTab] = useState('placements');

  const fetchUser = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/users/${userId}`;
      const params = [];
      if (dateFrom) params.push(`date_from=${dateFrom}`);
      if (dateTo) params.push(`date_to=${dateTo}`);
      if (params.length) url += '?' + params.join('&');
      const res = await axios.get(url, { withCredentials: true });
      setUserData(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [userId, dateFrom, dateTo]);

  useEffect(() => { if (userId) fetchUser(); }, [fetchUser, userId]);

  if (loading) return <div className="flex items-center justify-center py-20"><SpinnerGap size={32} className="animate-spin text-blue-600" /></div>;
  if (!userData) return <div className="text-center py-20 text-slate-400">User not found or access denied</div>;

  const canSeeSalary = isSuperadmin || isCeo;
  const u = userData;
  const approvalRate = u.placement_count > 0 ? Math.round((u.approved_count / u.placement_count) * 100) : 0;

  return (
    <div className="max-w-6xl mx-auto space-y-5" data-testid="user-profile-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="h-9 w-9" data-testid="back-btn">
          <ArrowLeft size={20} />
        </Button>
        <div className="flex items-center gap-4 flex-1">
          <div className="w-14 h-14 rounded-full bg-slate-200 flex items-center justify-center text-xl font-bold text-slate-600">
            {u.name?.charAt(0)}
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-['Outfit']">{u.name}</h1>
            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
              <Badge className={(ROLE_COLORS[u.role] || 'bg-slate-100 text-slate-600') + ' text-xs'}>{ROLE_LABELS[u.role] || u.role}</Badge>
              {u.designation && <span className="text-sm text-slate-500">{u.designation}</span>}
              {u.team && <Badge variant="outline" className="text-[10px]">{u.team}</Badge>}
              {u.organization && <Badge variant="outline" className="text-[10px]">{u.organization}</Badge>}
            </div>
          </div>
        </div>
      </div>

      {/* Date Filters */}
      <Card className="border-slate-200" data-testid="date-filter-card">
        <CardContent className="p-3 flex items-center gap-3 flex-wrap">
          <FunnelSimple size={16} className="text-slate-400" />
          <span className="text-xs text-slate-500 font-medium">Filter by date:</span>
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-8 w-40 text-xs" data-testid="date-from" />
          <span className="text-xs text-slate-400">to</span>
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-8 w-40 text-xs" data-testid="date-to" />
          {(dateFrom || dateTo) && (
            <Button variant="ghost" size="sm" onClick={() => { setDateFrom(''); setDateTo(''); }} className="text-xs h-8" data-testid="clear-filters">Clear</Button>
          )}
        </CardContent>
      </Card>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <Card className="border-slate-200"><CardContent className="p-3">
          <p className="text-[10px] uppercase text-slate-400 font-semibold">Total</p>
          <p className="text-xl font-bold text-blue-700">{u.placement_count || 0}</p>
        </CardContent></Card>
        <Card className="border-emerald-200 bg-emerald-50/30"><CardContent className="p-3">
          <p className="text-[10px] uppercase text-slate-400 font-semibold">Approved</p>
          <p className="text-xl font-bold text-emerald-700">{u.approved_count || 0}</p>
          <p className="text-[10px] text-emerald-600">{approvalRate}% rate</p>
        </CardContent></Card>
        <Card className="border-slate-200"><CardContent className="p-3">
          <p className="text-[10px] uppercase text-slate-400 font-semibold">Pending</p>
          <p className="text-xl font-bold text-amber-600">{u.pending_count || 0}</p>
        </CardContent></Card>
        <Card className="border-red-200 bg-red-50/30"><CardContent className="p-3">
          <p className="text-[10px] uppercase text-slate-400 font-semibold">Backouts</p>
          <p className="text-xl font-bold text-red-600">{u.backout_count || 0}</p>
        </CardContent></Card>
        <Card className="border-slate-200"><CardContent className="p-3">
          <p className="text-[10px] uppercase text-slate-400 font-semibold">Rejected</p>
          <p className="text-xl font-bold text-red-500">{u.rejected_count || 0}</p>
        </CardContent></Card>
        <Card className="border-slate-200"><CardContent className="p-3">
          <p className="text-[10px] uppercase text-slate-400 font-semibold">Supported</p>
          <p className="text-xl font-bold text-violet-600">{u.supported_count || 0}</p>
        </CardContent></Card>
        {u.salary > 0 && (
          <Card className="border-slate-200"><CardContent className="p-3">
            <p className="text-[10px] uppercase text-slate-400 font-semibold">Salary (Monthly)</p>
            <p className="text-xl font-bold text-blue-700">{fmtINR(u.salary)}</p>
          </CardContent></Card>
        )}
        {(u.total_margin || 0) > 0 && (
          <Card className="border-teal-200 bg-teal-50/30"><CardContent className="p-3">
            <p className="text-[10px] uppercase text-slate-400 font-semibold">Margin</p>
            <p className="text-xl font-bold text-teal-700">${u.total_margin?.toFixed(2)}</p>
            <p className="text-[10px] text-teal-500">Avg: ${u.avg_margin?.toFixed(2)}/hr</p>
          </CardContent></Card>
        )}
      </div>

      {/* Financial + Performance Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-slate-200">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-2 mb-1"><CurrencyInr size={16} className="text-emerald-600" /><span className="text-xs font-semibold text-slate-600 uppercase">Incentive Earned</span></div>
            <p className="text-2xl font-bold text-emerald-700" data-testid="total-incentive">{fmtINR(u.total_incentive || 0)}</p>
            <div className="flex justify-between text-xs text-slate-500">
              <span>Avg Margin</span>
              <span className="font-medium text-slate-700">${u.avg_margin?.toFixed(2) || '0.00'}</span>
            </div>
            <div className="flex justify-between text-xs text-slate-500">
              <span>Total Margin</span>
              <span className="font-medium text-slate-700">${u.total_margin?.toFixed(2) || '0.00'}</span>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-2 mb-1"><Target size={16} className="text-blue-600" /><span className="text-xs font-semibold text-slate-600 uppercase">Low-End & Conversion</span></div>
            <div className="flex justify-between text-xs text-slate-500">
              <span>Low-End Approved</span>
              <span className="font-bold text-slate-800">{u.low_end_approved || 0}</span>
            </div>
            <div className="flex justify-between text-xs text-slate-500">
              <span>Standard Equivalent (4:1)</span>
              <span className="font-bold text-blue-700">{u.low_end_standard_equivalent || 0}</span>
            </div>
            <div className="flex justify-between text-xs text-slate-500">
              <span>Remaining Low-End</span>
              <span className="font-medium text-amber-600">{(u.low_end_approved || 0) % 4} / 4</span>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-2 mb-1"><Handshake size={16} className="text-purple-600" /><span className="text-xs font-semibold text-slate-600 uppercase">Special Placements</span></div>
            <div className="flex justify-between text-xs text-slate-500">
              <span>New Vendor</span>
              <span className="font-bold text-cyan-700">{u.new_vendor_count || 0}</span>
            </div>
            <div className="flex justify-between text-xs text-slate-500">
              <span>New Client</span>
              <span className="font-bold text-emerald-700">{u.new_client_count || 0}</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Category Breakdown */}
      {u.category_breakdown && Object.keys(u.category_breakdown).length > 0 && (
        <Card className="border-slate-200">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-['Outfit']">Category Breakdown</CardTitle></CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {Object.entries(u.category_breakdown).sort((a, b) => b[1] - a[1]).map(([cat, count]) => (
                <div key={cat} className="flex items-center gap-2 px-3 py-1.5 bg-slate-50 rounded-lg border text-xs">
                  <span className="text-slate-600">{cat.replace(/_/g, ' ').toUpperCase()}</span>
                  <span className="font-bold text-slate-800">{count}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <div className="flex gap-1 border-b border-slate-200">
        {[
          { key: 'placements', label: 'Placements', count: u.placements_detail?.length },
          { key: 'supported', label: 'Supported', count: u.supported_placements_detail?.length },
          { key: 'utilization', label: 'Portal Utilization' },
          { key: 'claims', label: 'Onboarding Bonuses' },
          { key: 'info', label: 'Personal Info' },
        ].map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)}
            className={`px-4 py-2.5 text-xs font-medium border-b-2 transition-colors ${activeTab === tab.key ? 'border-blue-600 text-blue-700' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
            data-testid={`tab-${tab.key}`}>
            {tab.label} {tab.count !== undefined && <span className="ml-1 text-slate-400">({tab.count})</span>}
          </button>
        ))}
      </div>

      {/* Placements Table */}
      {activeTab === 'placements' && (
        <Card className="border-slate-200" data-testid="placements-table-card">
          <CardContent className="p-0">
            {!u.placements_detail?.length ? (
              <div className="text-center py-10 text-slate-400 text-sm">No placements found for this period</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-[10px] text-slate-400 uppercase tracking-wider border-b border-slate-100 bg-slate-50/50">
                      <th className="px-3 py-2.5 text-left font-semibold">Candidate</th>
                      <th className="px-3 py-2.5 text-left font-semibold">Category</th>
                      <th className="px-3 py-2.5 text-left font-semibold">Vendor / Client</th>
                      <th className="px-3 py-2.5 text-center font-semibold">Status</th>
                      <th className="px-3 py-2.5 text-right font-semibold">Bill Rate</th>
                      <th className="px-3 py-2.5 text-right font-semibold">Pay Rate</th>
                      <th className="px-3 py-2.5 text-right font-semibold">Margin</th>
                      <th className="px-3 py-2.5 text-right font-semibold">Incentive</th>
                      <th className="px-3 py-2.5 text-left font-semibold">Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {u.placements_detail.map((p) => (
                      <tr key={p.id} className={`hover:bg-slate-50/80 transition-colors ${p.is_low_end ? 'bg-amber-50/30' : ''}`} data-testid={`placement-row-${p.id}`}>
                        <td className="px-3 py-2.5">
                          <Link to={`/dashboard/placements/${p.id}`} className="text-blue-700 hover:text-blue-900 font-medium underline" data-testid={`placement-link-${p.id}`}>
                            {p.candidate_name}
                          </Link>
                          <div className="flex items-center gap-1 mt-0.5">
                            <Badge className={`text-[9px] ${p.module === 'recruiting' ? 'bg-teal-100 text-teal-700' : 'bg-blue-100 text-blue-700'}`}>
                              {p.module === 'recruiting' ? 'REC' : 'SAL'}
                            </Badge>
                            {p.is_low_end && <Badge className="bg-amber-100 text-amber-700 text-[9px]">LOW-END</Badge>}
                            {p.vendor_type === 'new' && <Badge className="bg-cyan-100 text-cyan-700 text-[9px]">NEW VENDOR</Badge>}
                            {p.vendor_type === 'existing_new' && <Badge className="bg-cyan-50 text-cyan-600 text-[9px]">EXIST-NEW VENDOR</Badge>}
                            {p.client_type === 'new' && <Badge className="bg-emerald-100 text-emerald-700 text-[9px]">NEW CLIENT</Badge>}
                            {p.is_new_technology && <Badge className="bg-purple-100 text-purple-700 text-[9px]">{p.new_technology_name || 'NEW TECH'}</Badge>}
                          </div>
                        </td>
                        <td className="px-3 py-2.5 text-slate-600">{(p.effective_category || p.category || '').replace(/_/g, ' ')}</td>
                        <td className="px-3 py-2.5">
                          <div className="text-slate-700">{p.vendor}</div>
                          <div className="text-slate-400">{p.client}</div>
                        </td>
                        <td className="px-3 py-2.5 text-center">
                          <Badge className={(STATUS_COLORS[p.status] || 'bg-slate-100 text-slate-600') + ' text-[9px]'}>
                            {p.status?.replace(/_/g, ' ').toUpperCase()}
                          </Badge>
                        </td>
                        <td className="px-3 py-2.5 text-right font-medium text-slate-700">${p.bill_rate?.toFixed(2)}</td>
                        <td className="px-3 py-2.5 text-right font-medium text-slate-700">${p.pay_rate?.toFixed(2)}</td>
                        <td className="px-3 py-2.5 text-right font-medium text-slate-700">${(p.margin || 0).toFixed(2)}</td>
                        <td className="px-3 py-2.5 text-right font-bold text-emerald-700">
                          {isApprovedStatus(p.status) ? fmtINR(p.total_incentive || 0) : '-'}
                        </td>
                        <td className="px-3 py-2.5 text-slate-500 whitespace-nowrap">
                          {p.created_at ? new Date(p.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }) : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Supported Placements Table */}
      {activeTab === 'supported' && (
        <Card className="border-slate-200" data-testid="supported-table-card">
          <CardContent className="p-0">
            {!u.supported_placements_detail?.length ? (
              <div className="text-center py-10 text-slate-400 text-sm">No supported placements found</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-[10px] text-slate-400 uppercase tracking-wider border-b border-slate-100 bg-slate-50/50">
                      <th className="px-3 py-2.5 text-left font-semibold">Candidate</th>
                      <th className="px-3 py-2.5 text-left font-semibold">Category</th>
                      <th className="px-3 py-2.5 text-left font-semibold">Vendor / Client</th>
                      <th className="px-3 py-2.5 text-center font-semibold">Status</th>
                      <th className="px-3 py-2.5 text-right font-semibold">Margin</th>
                      <th className="px-3 py-2.5 text-right font-semibold">Incentive</th>
                      <th className="px-3 py-2.5 text-left font-semibold">Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {u.supported_placements_detail.map((p) => (
                      <tr key={p.id} className="hover:bg-slate-50/80 transition-colors" data-testid={`supported-row-${p.id}`}>
                        <td className="px-3 py-2.5">
                          <Link to={`/dashboard/placements/${p.id}`} className="text-blue-700 hover:text-blue-900 font-medium underline">
                            {p.candidate_name}
                          </Link>
                        </td>
                        <td className="px-3 py-2.5 text-slate-600">{(p.category || '').replace(/_/g, ' ')}</td>
                        <td className="px-3 py-2.5">
                          <div className="text-slate-700">{p.vendor}</div>
                          <div className="text-slate-400">{p.client}</div>
                        </td>
                        <td className="px-3 py-2.5 text-center">
                          <Badge className={(STATUS_COLORS[p.status] || 'bg-slate-100 text-slate-600') + ' text-[9px]'}>
                            {p.status?.replace(/_/g, ' ').toUpperCase()}
                          </Badge>
                        </td>
                        <td className="px-3 py-2.5 text-right font-medium text-slate-700">${(p.margin || 0).toFixed(2)}</td>
                        <td className="px-3 py-2.5 text-right font-bold text-emerald-700">
                          {isApprovedStatus(p.status) ? fmtINR(p.total_incentive || 0) : '-'}
                        </td>
                        <td className="px-3 py-2.5 text-slate-500 whitespace-nowrap">
                          {p.created_at ? new Date(p.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }) : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Portal Utilization Tab */}
      {activeTab === 'utilization' && (
        <PortalUtilizationSection utilization={u.portal_utilization} />
      )}

      {/* Onboarding Bonuses Tab — vendor / client ownership claims */}
      {activeTab === 'claims' && (
        <VendorClaimsWidget userId={u.id} title={`${u.name}'s Vendor & Client Onboarding Bonuses`} />
      )}

      {/* Personal Info Tab */}
      {activeTab === 'info' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-['Outfit'] flex items-center gap-2"><User size={16} className="text-blue-600" /> Personal Info</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="flex items-center gap-2"><Envelope size={14} className="text-slate-400" /><span className="text-slate-600">{u.email}</span></div>
              {(u.direct_number || u.contact_number) && (
                <div className="flex items-center gap-2"><Phone size={14} className="text-slate-400" /><span className="text-slate-600">{u.direct_number || u.contact_number}</span></div>
              )}
              {u.employee_id && (
                <div className="flex justify-between"><span className="text-slate-400 text-xs">Employee ID</span><span className="text-slate-700 font-medium">{u.employee_id}</span></div>
              )}
              {(u.work_location || u.location) && (
                <div className="flex items-center gap-2"><MapPin size={14} className="text-slate-400" /><span className="text-slate-600">{u.work_location || u.location}</span></div>
              )}
              {canSeeSalary && u.salary && (
                <div className="flex justify-between border-t pt-2">
                  <span className="text-slate-400 text-xs">Salary</span>
                  <span className="text-slate-700 font-semibold">{fmtINR(u.salary)}</span>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-['Outfit'] flex items-center gap-2"><Buildings size={16} className="text-blue-600" /> Team & Organization</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              {u.team && <div className="flex justify-between"><span className="text-slate-400 text-xs">Team</span><span className="text-slate-700 font-medium">{u.team}</span></div>}
              {u.team && <div className="flex justify-between"><span className="text-slate-400 text-xs">Team</span><span className="text-slate-700">{u.team}</span></div>}
              {u.organization && <div className="flex justify-between"><span className="text-slate-400 text-xs">Organization</span><span className="text-slate-700">{u.organization}</span></div>}
            </CardContent>
          </Card>

          {/* Reporting Structure */}
          {((u.reporting_manager_details || []).length > 0 || (u.onsite_manager_details || []).length > 0) && (
            <Card className="border-slate-200 md:col-span-2">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-['Outfit'] flex items-center gap-2"><TreeStructure size={16} className="text-blue-600" /> Reporting Structure</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {(u.reporting_manager_details || []).length > 0 && (
                  <div>
                    <p className="text-[10px] uppercase text-slate-400 font-semibold mb-1.5">Reports To</p>
                    <div className="flex flex-wrap gap-2">
                      {u.reporting_manager_details.map(rm => (
                        <button key={rm.id} onClick={() => navigate(`/dashboard/user/${rm.id}`)} className="flex items-center gap-2 px-3 py-1.5 bg-slate-50 rounded-lg border hover:border-blue-300 hover:bg-blue-50/50 transition-colors cursor-pointer" data-testid={`rm-link-${rm.id}`}>
                          <div className="w-6 h-6 rounded-full bg-slate-200 flex items-center justify-center text-[10px] font-bold text-slate-600">{rm.name?.charAt(0)}</div>
                          <div className="text-left">
                            <p className="text-xs font-medium text-blue-700">{rm.name}</p>
                            <p className="text-[9px] text-slate-400">{ROLE_LABELS[rm.role] || rm.role}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {(u.onsite_manager_details || []).length > 0 && (
                  <div>
                    <p className="text-[10px] uppercase text-slate-400 font-semibold mb-1.5">Onsite Manager(s)</p>
                    <div className="flex flex-wrap gap-2">
                      {u.onsite_manager_details.map(om => (
                        <button key={om.id} onClick={() => navigate(`/dashboard/user/${om.id}`)} className="flex items-center gap-2 px-3 py-1.5 bg-orange-50 rounded-lg border border-orange-200 hover:border-orange-400 transition-colors cursor-pointer" data-testid={`om-link-${om.id}`}>
                          <div className="w-6 h-6 rounded-full bg-orange-100 flex items-center justify-center text-[10px] font-bold text-orange-600">{om.name?.charAt(0)}</div>
                          <div className="text-left">
                            <p className="text-xs font-medium text-orange-700">{om.name}</p>
                            <p className="text-[9px] text-slate-400">Onsite Manager</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
};

export default UserProfilePage;
